// src/App.js
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { styled } from 'baseui';
import { ThemeProvider as BaseThemeProvider, LightTheme, DarkTheme } from 'baseui';

import { AppConfigProvider } from './contexts/AppConfigContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdProvider } from './contexts/AdContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider as AppThemeProvider, useTheme } from './contexts/ThemeContext';

import GlobalAdArea from './components/common/GlobalAdArea';

import ControllerPage from './pages/ControllerPage';
import DisplayPage from './pages/DisplayPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import UpdatePasswordPage from './pages/UpdatePasswordPage';
import ManualPage from './pages/ManualPage';
import OwnerDashboardPage from './pages/OwnerDashboardPage';
import AdminLayout from './pages/admin/AdminLayout';
import timeSyncService from './services/TimeSyncService';


import { defaultAdHeight, mobileAdHeight } from './styles/displayPageStyles';

const AppWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
});

const ContentWrapper = styled('main', ({ $theme }) => ({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100vh - ${defaultAdHeight})`,
  backgroundColor: $theme.colors.backgroundPrimary,
  [`@media screen and (max-width: ${$theme.breakpoints.medium - 1}px), screen and (min-width: ${$theme.breakpoints.medium}px) and (max-width: ${$theme.breakpoints.large - 1}px) and (orientation: portrait)`]: {
    height: `calc(100vh - ${mobileAdHeight})`,
  },
  overflowY: 'auto',
}));

const AppRoutes = () => {
  const { t } = useLocale();
  const { passwordRecoveryMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (passwordRecoveryMode && location.pathname !== '/update-password') {
      navigate('/update-password');
    }
  }, [passwordRecoveryMode, navigate, location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<AuthRedirector />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
      <Route path="/update-password" element={<UpdatePasswordPage />} />
      <Route path="/manual" element={<ManualPage />} />
      <Route path="/t" element={<DisplayPage />} />
      <Route path="/t/:tournamentId" element={<DisplayPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><OwnerDashboardPage /></ProtectedRoute>} />
      <Route path="/controller/:tournamentId" element={<ProtectedRoute><ControllerPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute adminOnly={true}><AdminLayout /></ProtectedRoute>} />
      <Route path="*" element={<div>{t('app.notFound')}</div>} />
    </Routes>
  );
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, currentUser, loading } = useAuth();
  const { t } = useLocale();

  if (loading) {
    return <div>{t('app.authLoading')}</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && currentUser?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useLocale();

  if (loading) {
    return <div>{t('app.authLoading')}</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AuthRedirector = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useLocale();

  if (loading) {
    return <div>{t('app.loading')}</div>;
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

const ThemedApp = () => {
  const { themeMode } = useTheme();

  return (
    <BaseThemeProvider theme={themeMode === 'dark' ? DarkTheme : LightTheme}>
      <AppWrapper>
        <GlobalAdArea />
        <ContentWrapper>
          <AppRoutes />
        </ContentWrapper>
      </AppWrapper>
    </BaseThemeProvider>
  );
};


function App() {
  useEffect(() => {
    timeSyncService.sync();
  }, []);

  return (
    <AppConfigProvider>
      <AppThemeProvider>
        <LocaleProvider>
          <NotificationProvider>
            <AdProvider>
              <AuthProvider>
                <Router basename="/">
                  <ThemedApp />
                </Router>
              </AuthProvider>
            </AdProvider>
          </NotificationProvider>
        </LocaleProvider>
      </AppThemeProvider>
    </AppConfigProvider>
  );
}

export default App;